"use client"

import { yupResolver } from "@hookform/resolvers/yup"
import { useRouter } from "next/navigation"
import { useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import { RiCloseCircleLine, RiSendPlane2Line } from "react-icons/ri"
import { toast } from "react-toastify"
import * as Yup from "yup"

import { Button } from "@/components/Button"
import { ErrorText } from "@/components/ErrorText"
import { Paragraph } from "@/components/Paragraph"
import { useLocaleConfig } from "@/providers"
import { useLocaleStrings } from "@/providers/localeStrings/client"
import theme from "@/theme"

import { Card, Footer, H2, Input, Loader } from "./styled"

/** This component renders the forgot password form card. */
export const FormCard = () => {
  const locale = useLocaleConfig().locale ?? "de-DE"
  const router = useRouter()
  const [isLoading, setIsLoading] = useState(false)

  const {
    AUTH_FORGOT_PW_BUTTON_CANCEL,
    AUTH_FORGOT_PW_BUTTON_SUBMIT,
    AUTH_FORGOT_PW_EMAIL_LABEL,
    AUTH_FORGOT_PW_HINT,
    AUTH_FORGOT_PW_TITLE,
    ERROR_MSG_EMAIL_INVALID,
    ERROR_MSG_REQUIRED,
    PASSWORD_TOKEN_GENERATE_SUCCESS,
    ERROR_MSG_PASSWORD_TOKEN_GENERATE,
  } = useLocaleStrings()

  const forgotPasswordSchema = useMemo(
    () =>
      Yup.object({
        email: Yup.string().trim().email(ERROR_MSG_EMAIL_INVALID).required(ERROR_MSG_REQUIRED),
      }).required(),
    [ERROR_MSG_EMAIL_INVALID, ERROR_MSG_REQUIRED],
  )

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: yupResolver(forgotPasswordSchema) })

  const onSubmit = async ({ email }) => {
    if (isLoading) return

    setIsLoading(true)
    const request = await fetch("/api/password-reset", {
      method: "POST",
      body: JSON.stringify({ email, locale }),
    })
    setIsLoading(false)

    if (request.status === 500) {
      toast.error(ERROR_MSG_PASSWORD_TOKEN_GENERATE, { theme: "dark" })

      return
    }

    const response = await request.json()

    if (response?.message === "MESSAGE_SENT") {
      toast.success(PASSWORD_TOKEN_GENERATE_SUCCESS, { theme: "dark" })
      router.replace(`/${locale}/auth/signin`)
    } else {
      toast.error(ERROR_MSG_PASSWORD_TOKEN_GENERATE, { theme: "dark" })
    }
  }

  return (
    <Card>
      <form onSubmit={handleSubmit(onSubmit)}>
        <H2>{AUTH_FORGOT_PW_TITLE}</H2>

        <Paragraph>{AUTH_FORGOT_PW_HINT}</Paragraph>

        <Input
          label={AUTH_FORGOT_PW_EMAIL_LABEL}
          error={errors.email && <ErrorText message={errors.email.message} />}
          {...register("email")}
        />

        <Footer>
          <Button rounded secondary submit disabled={isLoading}>
            {isLoading ? <Loader /> : <RiSendPlane2Line size={theme.iconSizes.normal} />}
            {AUTH_FORGOT_PW_BUTTON_SUBMIT}
          </Button>

          <Button href={`/${locale}/auth/signin`} rounded seo disabled={isLoading}>
            <RiCloseCircleLine size={theme.iconSizes.normal} />
            {AUTH_FORGOT_PW_BUTTON_CANCEL}
          </Button>
        </Footer>
      </form>
    </Card>
  )
}
